<script setup lang="ts">
import { useField, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import * as zod from "zod";
import { onMounted, ref } from "vue";
import router from "@/router";
import BaseLoading from "@/components/Common/BaseLoading.vue";
import { toast } from "vue-sonner";
import { useItemStore } from "@/stores/management/item";
import { ItemDataPayload, ItemDataResponse } from "@/types/interface/item";
import { mapOptionMultiselect, urlToObject } from "@/utils/helper";
import { VarianDataPayload } from "@/types/interface/varian";
import { useVarianStore } from "@/stores/management/varian";
import type { Header } from "vue3-easy-data-table";
import EasyDataTable from "vue3-easy-data-table";
import "vue3-easy-data-table/dist/style.css";
import Swal from "sweetalert2";
import { hasAccess } from "@/utils/helper";

const idItem = ref<string>("");
const isEdit = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const itemStore = useItemStore();
const varianStore = useVarianStore();
const detailItem = ref<ItemDataResponse>();
const listItem = ref<any>();
const page = ref(1);
const limit = ref(10);
const showTable = ref(false);
const accessPermission = ref<any>([]);

const validationSchema = toTypedSchema(
  zod.object({
    goods_id: zod.preprocess(
      (val) => (val && typeof val === "object" ? val.value : val),
      zod.number()
    ),
    // msg_code: zod.string().nonempty({ message: "MSG Code is required" }),
    impa_code: zod.string().nonempty({ message: "Impa Code is required" }),
    keyVarian: zod.string().nonempty({ message: "Key is required" }),
    valueVarian: zod.string().nonempty({ message: "Varian is required" }),
  })
);

const { handleSubmit, errors } = useForm({
  validationSchema,
});

const { value: goods_id } = useField<string>("goods_id");
const { value: msg_code } = useField<string>("msg_code");
const { value: impa_code } = useField<string>("impa_code");
const { value: keyVarian } = useField<string>("keyVarian");
const { value: valueVarian } = useField<string>("valueVarian");

const clearForm = () => {
  msg_code.value = "";
  impa_code.value = "";
  keyVarian.value = "";
  valueVarian.value = "";
};

const onSubmit = handleSubmit(async (values) => {
  isLoading.value = true;
  const payloadVarian: VarianDataPayload = {
    goods_id: goods_id.value,
    impa_code: impa_code.value,
    msg_code: msg_code.value,
    key: keyVarian.value,
    value: valueVarian.value,
  };

  const responseApi = ref<any>();
  if (isEdit.value == true) {
    responseApi.value = await varianStore.dispatchUpdate(
      idItem.value,
      payloadVarian
    );
  } else {
    responseApi.value = await varianStore.dispatchCreate(payloadVarian);
  }

  const { success, message } = responseApi.value;
  if (success == true) {
    setTimeout(() => {
      isLoading.value = false;
      toast.success(
        isEdit.value
          ? "Varian Successfully Updated"
          : "Varian Successfully Added"
      );
      clearForm();
      loadDetailItem();
      window.location.reload();
    }, 1000);
  } else {
    isLoading.value = false;
    toast.error(message);
  }
});

const loadItems = async () => {
  const { success, message } = await itemStore.dispatchGetItems(1, 99);
  if (success) {
    listItem.value = mapOptionMultiselect(
      await itemStore.items.items,
      "id",
      "name"
    );
  }
};

loadItems();

const loadDetailItem = async () => {
  const { success, message } = await itemStore.dispatchDetail(idItem.value);

  if (success) {
    detailItem.value = await itemStore?.item?.data;
    goods_id.value = detailItem.value?.id;
    showTable.value = true;
  }
};

onMounted(() => {
  if (router.currentRoute.value.params.id != undefined) {
    idItem.value = router.currentRoute.value.params.id.toString();
    loadDetailItem();
  }
});

const handleDelete = async (item: any) => {
  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      cancelButton: "btn btn-danger",
      confirmButton: "btn btn-success",
    },
    buttonsStyling: false,
  });
  swalWithBootstrapButtons
    .fire({
      title: `Are you sure want to delete data ${item.name} ?`,
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    })
    .then(async (result) => {
      if (result.isConfirmed) {
        const { success, message } = await varianStore.dispatchDelete(item.id);
        if (success) {
          swalWithBootstrapButtons.fire({
            title: "Deleted!",
            text: "Your file has been deleted.",
            icon: "success",
          });
          loadDetailItem();
        } else {
          toast.error(message);
        }
      } else if (result.dismiss === Swal.DismissReason.cancel) {
        swalWithBootstrapButtons.fire({
          title: "Cancelled",
          text: "Your action has been aborted",
          icon: "error",
        });
      }
    });
};

const headers: Header[] = [
  { text: "MSG CODE", value: "msg_code" },
  { text: "IMPA CODE", value: "impa_code" },
  { text: "TYPE", value: "key" },
  { text: "VALUE", value: "value" },
  { text: "ACTION", value: "action" },
];
</script>
<template>
  <div class="row justify-content-center">
    <div class="col-lg-5">
      <div class="card card-catalog">
        <div class="mb-4 transition-y card-body">
          <div class="position-relative mb-3">
            <div class="image-box">
              <img
                :src="detailItem?.image_link"
                class="rounded-3"
                alt="detailItem?"
              />
            </div>
          </div>
          <div class="row">
            <div class="col-12 fs-15 mb-0">
              MSG CODE :
              <span class="fw-medium">{{ detailItem?.msg_code }}</span>
            </div>
            <div class="col-12 fs-15 mb-0">
              IMPA CODE :
              <span class="fw-medium">{{ detailItem?.impa_code }}</span>
            </div>
            <div class="col-12 fs-15 mb-0">
              Category :
              <span class="fw-medium">{{ detailItem?.category?.name }}</span>
            </div>
            <div class="col-12 fs-15 mb-0">
              Group Category :
              <span class="fw-medium">{{
                detailItem?.groupCategory?.name
              }}</span>
            </div>
            <div class="col-12 fs-15 mb-0">
              Item :
              <span class="fw-bold">{{ detailItem?.name }}</span>
            </div>
            <div class="col-12 fs-15 mb-0">
              Unit :
              <span class="fw-medium">{{ detailItem?.unitType?.name }}</span>
            </div>
            <div class="col-12 fs-15 mb-0">
              Size :
              <span class="fw-medium">{{ detailItem?.size }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-lg-7">
      <div class="card bg-white border-0 rounded-3 mb-4">
        <div class="card-body p-4">
          <BaseLoading text="Processing Data..." v-if="isLoading" />
          <div
            class="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3 mb-lg-4"
          >
            <h3 class="mb-0" v-if="hasAccess('goods_variant-create')">Form Varian Item</h3>
            <a
              class="btn btn-danger fw-semibold text-white px-3"
              type="button"
              href="/management/item"
            >
              <i class="ri-arrow-drop-left-line text-gray-light"></i>
              Back To List Item
            </a>
          </div>
          <form
            @submit="onSubmit"
            class="row g-3 needs-validation form"
            v-if="!isLoading && hasAccess('goods_variant-create')"
          >
            <div class="row">
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="msg_code" class="form-label label text-secondary"
                    >MSG Code</label
                  >
                  <div class="row">
                    <div class="col-lg-8">
                      <div class="position-relative">
                        <input
                          name="msg_code"
                          :value="detailItem?.msg_code+'.'"
                          readonly="true"
                          type="text"
                          class="form-control text-dark"
                        />
                      </div>
                    </div>
                    <div class="col-lg-4">
                      <div class="position-relative">
                        <input
                          name="msg_code"
                          v-model="msg_code"
                          placeholder=""
                          type="text"
                          class="form-control text-dark"
                        />
                      </div>
                    </div>
                  </div>
                  <span class="text-danger">{{ errors.msg_code }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="impa_code" class="form-label label text-secondary"
                    >Impa Code <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="impa_code"
                      v-model="impa_code"
                      placeholder="Impa Code"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.impa_code }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="key" class="form-label label text-secondary"
                    >Varian Type <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="keyVarian"
                      v-model="keyVarian"
                      placeholder="Varian Type"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.keyVarian }}</span>
                </div>
              </div>
              <div class="col-lg-6">
                <div class="form-group">
                  <label for="value" class="form-label label text-secondary"
                    >Varian Value <span class="text-danger">*</span></label
                  >
                  <div class="position-relative">
                    <input
                      name="valueVarian"
                      v-model="valueVarian"
                      placeholder="Varian Value"
                      type="text"
                      class="form-control text-dark"
                    />
                  </div>
                  <span class="text-danger">{{ errors.valueVarian }}</span>
                </div>
              </div>
              <div class="col-12 mt-3">
                <button
                  class="btn btn-primary fw-semibold text-white py-2 px-3"
                  type="submit"
                >
                  Add Varian
                </button>
              </div>
            </div>
          </form>
          <div class="table-content mt-5" v-if="showTable && !isLoading">
            <EasyDataTable :headers="headers" :items="detailItem?.variant">
              <template #item-action="props" v-if="hasAccess('goods_variant-delete')">
                <div class="d-flex gap-2">
                  <button
                    class="btn btn-danger btn-sm"
                    @click="handleDelete(props)"
                  >
                    Delete
                  </button>
                </div>
              </template>
            </EasyDataTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
